import { useQuery } from "@tanstack/react-query"
import { getBackofficeSupportActivityQuery } from "modules/backoffice/lib/support/getBackofficeSupportActivityQuery"
import { getBackofficeSupportDeflectionActivityQuery } from "modules/backoffice/lib/support/getBackofficeSupportDeflectionActivityQuery"
import { getBackofficeSupportDomainActivityQuery } from "modules/backoffice/lib/support/getBackofficeSupportDomainActivityQuery"
import { getBackofficeSupportQuestionsQuery } from "modules/backoffice/lib/support/getBackofficeSupportQuestionsQuery"
import { getBackofficeSupportTreemapQuery } from "modules/backoffice/lib/support/getBackofficeSupportTreemapQuery"
import { Pagination } from "modules/shared/components/Pagination"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSearchQuery } from "modules/shared/hooks/useSearchQuery"
import { useMemo, useState } from "react"
import {
  BackofficeSupportTreemapDomainEnum,
  ClusterDomainEnum,
  GetBackofficeSupportQuestionsSchema,
  getTimeRange,
  resolveTimeDimension,
  TimeDimension,
  TimeFrame,
  TimeFrameEnum,
} from "schema"
import { z } from "zod"
import { BackofficeSupportActivity } from "./BackofficeSupportActivity"
import { BackofficeSupportDeflectionActivity } from "./BackofficeSupportDeflectionActivity"
import { BackofficeSupportDomainActivity } from "./BackofficeSupportDomainActivity"
import { BackofficeSupportFilters } from "./BackofficeSupportFilters"
import { BackofficeSupportQuestionsTable } from "./BackofficeSupportQuestionsTable"
import { BackofficeSupportTreemap } from "./BackofficeSupportTreemap"

const ITEMS_PER_PAGE = 50

type BackofficeSupportProps = {
  search: z.infer<typeof GetBackofficeSupportQuestionsSchema>
  routeFullPath: string
  onFilterByChange: (
    filterBy: z.infer<typeof GetBackofficeSupportQuestionsSchema>["filterBy"],
  ) => void
  onTimeRangeChange: (value: { timeFrame: TimeFrame; timeDimension: TimeDimension }) => void
}

type TreemapDomain = (typeof BackofficeSupportTreemapDomainEnum)[number]

export const BackofficeSupport = ({
  search,
  routeFullPath,
  onFilterByChange,
  onTimeRangeChange,
}: BackofficeSupportProps) => {
  const [selectedDomain, setSelectedDomain] = useState<TreemapDomain>(
    search.filterBy?.canonicalTopic?.domain ?? ClusterDomainEnum.Support,
  )
  const selectedTimeFrame = search.timeFrame ?? TimeFrameEnum.Last1Week
  const selectedTimeDimension = resolveTimeDimension(
    getTimeRange(selectedTimeFrame),
    search.timeDimension,
  )
  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: routeFullPath,
    total: ITEMS_PER_PAGE,
  })

  const { searchQuery, query, setQuery } = useSearchQuery({
    search,
    fullPath: routeFullPath,
    onSearchQueryChange: () => setPage(1),
  })

  const queryOptions = getBackofficeSupportQuestionsQuery({
    query: {
      query: query ?? "",
      page,
      total: ITEMS_PER_PAGE,
      filterBy: search.filterBy,
      timeFrame: selectedTimeFrame,
    },
    onSuccess: (data) => {
      setCount(data.pageInfo.count)
    },
  })

  const treemapFilterBy = useMemo(() => {
    if (!search.filterBy) return undefined

    const rest = { ...search.filterBy }
    delete rest.category
    delete rest.canonicalTopic
    return Object.keys(rest).length > 0 ? rest : undefined
  }, [search.filterBy])

  const { data: questions, isPending } = useQuery(queryOptions)
  const { data: treemapData, isPending: isTreemapPending } = useQuery(
    getBackofficeSupportTreemapQuery({
      query: {
        query: query ?? "",
        filterBy: treemapFilterBy,
        timeFrame: selectedTimeFrame,
        domain: selectedDomain,
      },
    }),
  )
  const { data: activityData, isPending: isActivityPending } = useQuery(
    getBackofficeSupportActivityQuery({
      query: {
        query: query ?? "",
        filterBy: search.filterBy,
        timeFrame: selectedTimeFrame,
        timeDimension: selectedTimeDimension,
      },
      enabled: selectedDomain === ClusterDomainEnum.Support,
    }),
  )
  const { data: deflectionActivityData, isPending: isDeflectionActivityPending } = useQuery(
    getBackofficeSupportDeflectionActivityQuery({
      query: {
        query: query ?? "",
        filterBy: search.filterBy,
        timeFrame: selectedTimeFrame,
        timeDimension: selectedTimeDimension,
      },
      enabled: selectedDomain === ClusterDomainEnum.Support,
    }),
  )
  const { data: domainActivityData, isPending: isDomainActivityPending } = useQuery(
    getBackofficeSupportDomainActivityQuery({
      query: {
        query: query ?? "",
        filterBy: search.filterBy,
        timeFrame: selectedTimeFrame,
        timeDimension: selectedTimeDimension,
        domain: selectedDomain,
      },
      enabled: selectedDomain !== ClusterDomainEnum.Support,
    }),
  )

  const isAmphoraTicketsQuickFilterActive =
    !search.filterBy?.companyId &&
    search.filterBy?.integration?.join() === "amphora" &&
    search.filterBy?.status === "hasTicket" &&
    search.filterBy?.isLogistics === "yes"

  function updateFilterBy(
    nextFilterBy: z.infer<typeof GetBackofficeSupportQuestionsSchema>["filterBy"],
  ) {
    if (!nextFilterBy || Object.keys(nextFilterBy).length === 0) {
      onFilterByChange(undefined)
      return
    }

    onFilterByChange(nextFilterBy)
  }

  function updateFilters(changes: Partial<NonNullable<typeof search.filterBy>>) {
    updateFilterBy({ ...search.filterBy, ...changes })
  }

  function applyAmphoraTicketsQuickFilter() {
    updateFilterBy({
      integration: ["amphora"],
      status: "hasTicket",
      isLogistics: "yes",
    })
  }

  function toggleTopicFromTreemapNode(nodeId: string) {
    const clickedLabel = treemapData?.find((item) => String(item.id) === nodeId)?.label
    if (!clickedLabel) return

    const nextFilterBy = { ...(search.filterBy ?? {}) }
    const selectedTopic = nextFilterBy.canonicalTopic

    if (selectedTopic?.domain === selectedDomain && selectedTopic.label === clickedLabel) {
      delete nextFilterBy.canonicalTopic
    } else {
      nextFilterBy.canonicalTopic = { domain: selectedDomain, label: clickedLabel }
    }

    updateFilterBy(nextFilterBy)
  }

  function updateDomain(domain: TreemapDomain) {
    setSelectedDomain(domain)

    if (!search.filterBy?.canonicalTopic) return

    const nextFilterBy = { ...search.filterBy }
    delete nextFilterBy.canonicalTopic
    updateFilterBy(nextFilterBy)
  }

  return (
    <>
      <BackofficeSupportFilters
        filterBy={search.filterBy}
        onFilterChange={updateFilters}
        onClearFilters={() => {
          onFilterByChange(undefined)
          setQuery("")
        }}
        query={searchQuery ?? ""}
        setQuery={setQuery}
        selectedTimeFrame={selectedTimeFrame}
        selectedTimeDimension={selectedTimeDimension}
        onTimeRangeChange={onTimeRangeChange}
        isAmphoraTicketsQuickFilterActive={isAmphoraTicketsQuickFilterActive}
        onApplyAmphoraTicketsQuickFilter={applyAmphoraTicketsQuickFilter}
        selectedDomain={selectedDomain}
        domainOptions={[...BackofficeSupportTreemapDomainEnum]}
        onDomainChange={updateDomain}
      />
      <div className="flex flex-col gap-4">
        <BackofficeSupportTreemap
          key={selectedDomain}
          data={treemapData}
          isPending={isTreemapPending}
          onTopicClick={toggleTopicFromTreemapNode}
          domain={selectedDomain}
        />
        {selectedDomain === ClusterDomainEnum.Support ? (
          <>
            <BackofficeSupportActivity data={activityData} isPending={isActivityPending} />
            <BackofficeSupportDeflectionActivity
              data={deflectionActivityData}
              isPending={isDeflectionActivityPending}
            />
          </>
        ) : (
          <BackofficeSupportDomainActivity
            domain={selectedDomain}
            data={domainActivityData}
            isPending={isDomainActivityPending}
          />
        )}
        <BackofficeSupportQuestionsTable data={questions} isPending={isPending} />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          count={count}
        />
      </div>
    </>
  )
}
